import { useEffect, useState, useCallback, useRef } from "react";
import { FileText, Calendar } from "lucide-react";
import {
  GetRevenueMonitorByBrand,
  RevenueMonitorItem,
} from "../../../services/revenueservice";
import Swal from "sweetalert2";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const YEARS = ["2026", "2025", "2024", "2023", "2022", "2021"];

export default function RevenueByBrand() {
  const [selectedMonth, setSelectedMonth] = useState(MONTHS[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState<RevenueMonitorItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [tableWidth, setTableWidth] = useState(0);

  const monthIndex = MONTHS.indexOf(selectedMonth) + 1;
  const daysInMonth =
    selectedMonth && selectedYear
      ? new Date(Number(selectedYear), monthIndex, 0).getDate()
      : 31;
  const dayHeaders = Array.from({ length: daysInMonth }, (_, i) =>
    String(i + 1).padStart(2, "0")
  );

  const isReady = !!(selectedMonth && selectedYear);

  const fetchData = useCallback(async () => {
    if (!isReady) { setData([]); return; }
    setLoading(true);
    setError(null);
    setData([]);
    try {
      const result = await GetRevenueMonitorByBrand(monthIndex, parseInt(selectedYear, 10));
      setData(result?.revenueMonitorDatas ?? []);
    } catch {
      setError("Gagal memuat data revenue");
      Swal.fire({ icon: "error", title: "Error", text: "Gagal memuat data revenue" });
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [isReady, monthIndex, selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (tableRef.current) {
      setTableWidth(tableRef.current.scrollWidth);
    }
  }, [data]);

  const isSyncing = useRef(false);

  const syncScroll = (source: "top" | "table") => {
    if (isSyncing.current) return;
    isSyncing.current = true;
    const scrollLeft = source === "top"
      ? topScrollRef.current?.scrollLeft ?? 0
      : tableScrollRef.current?.scrollLeft ?? 0;

    if (source === "top" && tableScrollRef.current) {
      tableScrollRef.current.scrollLeft = scrollLeft;
    }
    if (source === "table" && topScrollRef.current) {
      topScrollRef.current.scrollLeft = scrollLeft;
    }
    isSyncing.current = false;
  };

  const parseDate = (dateStr: string): Date | null => {
    const match = dateStr.match(new RegExp("/Date\\((\\d+)\\)/"));
    if (match) return new Date(parseInt(match[1], 10));
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? null : d;
  };

  const mallNames = [...new Set(data.map((d) => d.MallName))];

  const getStatusForDay = (mall: string, dayNum: number): "check" | "cross" | "empty" => {
    const year = parseInt(selectedYear, 10);
    const entries = data.filter((d) => d.MallName === mall);
    if (entries.length === 0) return "empty";
    const found = entries.find((d) => {
      const dt = parseDate(d.TransactionDate);
      return dt && dt.getFullYear() === year && dt.getMonth() + 1 === monthIndex && dt.getDate() === dayNum;
    });
    if (found) return found.Amount > 0 ? "check" : "cross";
    return "cross";
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Calendar className="w-4 h-4 text-indigo-600" /> Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="">-- Choose One --</option>
                {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Calendar className="w-4 h-4 text-indigo-600" /> Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer dark:bg-gray-700 dark:text-white dark:border-gray-600"
              >
                <option value="">-- Choose One --</option>
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading data...</p>
        </div>
      )}

      {error && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex items-center justify-center py-16 text-red-500 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">

          {/* Top scrollbar */}
          {data.length > 0 && (
            <div
              ref={topScrollRef}
              onScroll={() => syncScroll("top")}
              className="overflow-x-auto"
              style={{ height: "12px" }}
            >
              <div style={{ width: tableWidth, height: "1px" }} />
            </div>
          )}

          {/* Single table with thead + tbody — header & data guaranteed to align */}
          <div
            ref={tableScrollRef}
            onScroll={() => syncScroll("table")}
            className="overflow-x-auto"
          >
            <table ref={tableRef} className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                  <th className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-2 sm:px-4 py-3 text-left font-semibold text-indigo-500 dark:text-indigo-400 w-9 sm:w-12 min-w-[36px] sm:min-w-[48px] border-r border-gray-100 dark:border-gray-700">
                    No
                  </th>
                  <th className="sticky left-9 sm:left-12 z-10 bg-white dark:bg-gray-800 px-2 sm:px-4 py-3 text-left font-semibold text-indigo-500 dark:text-indigo-400 w-[120px] sm:w-[200px] min-w-[120px] sm:min-w-[230px] max-w-[120px] sm:max-w-[230px] border-r border-gray-100 dark:border-gray-700">
                    Mall
                  </th>
                  {dayHeaders.map((d) => (
                    <th key={d} className="px-1 py-3 text-center font-semibold text-indigo-500 dark:text-indigo-400 w-[36px] min-w-[36px]">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mallNames.map((mall, idx) => (
                  <tr
                    key={idx}
                    className="group border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 px-2 sm:px-4 py-3 text-center text-gray-500 dark:text-gray-400 font-medium w-9 sm:w-12 min-w-[36px] sm:min-w-[48px] border-r border-gray-100 dark:border-gray-700">
                      {idx + 1}
                    </td>
                    <td
                      className="sticky left-9 sm:left-12 z-10 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 px-2 sm:px-4 py-3 text-gray-700 dark:text-gray-200 font-medium border-r border-gray-100 dark:border-gray-700 w-[120px] sm:w-[200px] min-w-[120px] sm:min-w-[230px] max-w-[120px] sm:max-w-[230px] truncate overflow-hidden whitespace-nowrap"
                      title={mall}
                    >
                      {mall}
                    </td>
                    {dayHeaders.map((d, di) => {
                      const status = getStatusForDay(mall, parseInt(d, 10));
                      return (
                        <td
                          key={di}
                          className={`px-1 py-1 text-center w-[36px] min-w-[36px] ${status === "check" ? "bg-green-500" : status === "cross" ? "bg-red-500" : ""
                            }`}
                        >
                          {status === "check" ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white">
                              <svg className="w-3 h-3 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
                              </svg>
                            </span>
                          ) : status === "cross" ? (
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white">
                              <svg className="w-3 h-3 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </span>
                          ) : (
                            <span className="inline-flex items-center justify-center w-6 h-6">
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-500" />
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && isReady && data.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">There are no records to display</p>
        </div>
      )}

      {!loading && !isReady && data.length === 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-16 h-16 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center">
            <FileText className="w-8 h-8 text-gray-300" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Pilih Month dan Year untuk menampilkan data</p>
        </div>
      )}
    </div>
  );
}